import { Eye, EyeSlash } from '@phosphor-icons/react'
import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { client } from '../api/client'
import { LanguageSwitcher } from '../components/LanguageSwitcher'
import i18n from '../i18n/config'
import { useAuthStore } from '../stores/auth'

interface LoginForm {
  username: string
  password: string
}

export function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation('auth')
  const { setToken, setParent } = useAuthStore()
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  const { register, handleSubmit, formState: { errors } } = useForm<LoginForm>()

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  const onSubmit = async (data: LoginForm) => {
    try {
      const response = await client.post('/auth/login/', data)
      const { token, parent } = response.data
      setToken(token)
      setParent(parent)

      // Update language preference if user has one set
      if (parent.language_preference) {
        i18n.changeLanguage(parent.language_preference)
      }

      // If email is not verified, redirect to verification page
      if (!parent.is_email_verified) {
        navigate('/verification-pending', { replace: true })
        return
      }

      // If email is verified, proceed to the intended destination
      const from = (location.state as any)?.from?.pathname || '/'
      navigate(from, { replace: true })
    } catch (err: any) {
      setError(err.response?.data?.error || t('login.error'))
    }
  }

  return (
    <>
      {/* Fixed Language Switcher */}
      <div className="fixed top-4 right-4 z-50">
        <LanguageSwitcher />
      </div>

      <div className="auth-container" style={{ minHeight: '100vh' }}>
        <div className="max-w-md w-full">
          {/* Standalone Logo */}
          <div className="flex justify-center mb-8">
            <img src="/images/logo.png" alt="Logo" />
          </div>

          <div className="card-neobrutalism mb-8">
            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)} autoComplete="off">
              {error && (
                <div className="bg-red-100 border-2 border-black p-4">
                  <div className="text-sm font-bold text-red-900">{error}</div>
                </div>
              )}
              
              <div className="space-y-4">
                <div>
                  <label htmlFor="email-input" className="block text-sm font-bold text-neobrutalism-text mb-2">
                    {t('login.emailLabel')}
                  </label>
                  <input
                    id="email-input"
                    {...register('username', { required: t('login.emailRequired') })}
                    type="email"
                    autoComplete="off"
                    spellCheck="false"
                    className="input-neobrutalism w-full"
                    placeholder={t('login.emailPlaceholder')}
                  />
                  {errors.username && (
                    <p className="mt-1 text-sm font-bold text-red-600">{errors.username.message}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="password-input" className="block text-sm font-bold text-neobrutalism-text mb-2">
                    {t('login.passwordLabel')}
                  </label>
                  <div className="relative">
                    <input
                      id="password-input"
                      {...register('password', { required: t('login.passwordRequired') })}
                      type={showPassword ? "text" : "password"}
                      autoComplete="new-password"
                      className="input-neobrutalism w-full pr-10"
                      placeholder={t('login.passwordPlaceholder')}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      onClick={togglePasswordVisibility}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="mt-1 text-sm font-bold text-red-600">{errors.password.message}</p>
                  )}
                </div>
              </div>

              <button 
                type="submit" 
                className="btn-neobrutalism w-full text-xs sm:text-base py-2 sm:py-3 px-2 sm:px-4"
              >
                {t('login.submitButton')}
              </button>
            </form>
          </div>

          <div className="card-neobrutalism">
            <p className="text-center text-lg">
              {t('login.noAccount')}{' '}
              <Link to="/register" className="link-neobrutalism">
                {t('login.registerLink')}
              </Link>
            </p>
          </div>
          
          <div className="card-neobrutalism mt-4">
            <p className="text-center text-md">
              {t('login.forgotPassword')}{' '}
              <Link to="/reset-password" className="link-neobrutalism">
                {t('login.resetPasswordLink')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </>
  )
} 